import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { useNotificationStore } from '../shared/store/notification.store';
import type { IResponseErrorApi, IResponseMessage } from '../shared/interfaces/global.interface';
import { extractErrorDetails, isDirectTranslationKey } from '../utils/extract-error-message';
import { useAuthStore } from '@/app/[locale]/auth/(presentation)/store/auth.store';
import { logger } from '@/core/utils/logger';
import { addFrontendBreadcrumb, reportFrontendError } from '@/core/monitoring/frontend-error-reporter';
import {
  isExpectedEmailTemplateEntitlementError,
  isExpectedTraceNotFound,
  isSensitiveEmailTemplateRequest,
  redactLoggablePayload,
  REDACTED,
} from './http-client-log-redaction';
import { hasResendCooldownMessage, RESEND_COOLDOWN_STATUS } from './email-code-cooldown';

// Define custom types for better type safety
export interface IApiResponse<TData> {
  data: TData;
  message?: string;
  status: number;
  /** Cabeceras de respuesta (solo POST por ahora; p. ej. `content-disposition` de un binario). */
  headers?: Record<string, string | undefined>;
}

export interface IHttpClientConfig {
  baseURL?: string;
  timeout?: number;
}

/**
 * 428 Precondition Required: el pedido es válido pero falta un paso previo.
 * Lo usa el gate de código por correo antes de firmar.
 */
const HTTP_PRECONDITION_REQUIRED = 428;

const KNOWN_ERROR_TRANSLATIONS = new Map<string, string>([
  ['USER_NOT_FOUND', 'common.AUTH.ERRORS.USER.NOT_FOUND'],
  ['INVALID_CREDENTIALS', 'common.AUTH.MESSAGES.INVALID_CREDENTIALS'],
  ['INVALID_CODE', 'common.AUTH.MESSAGES.INVALID_CODE'],
  ['CODE_EXPIRED', 'common.AUTH.MESSAGES.CODE_EXPIRED'],
  ['USER_ALREADY_EXISTS', 'common.AUTH.MESSAGES.USER_ALREADY_EXISTS'],
  ['PASSWORD_NOT_STRONG', 'common.AUTH.ERRORS.PASSWORD_NOT_STRONG'],
  ['ORGANIZATIONS.ERRORS.OWNER_ALREADY_ASSIGNED', 'common.ORGANIZATIONS.ERRORS.OWNER_ALREADY_ASSIGNED'],
  ['DOCUMENT_ALREADY_COMPLETED', 'common.DOCUMENTS.ERRORS.DOCUMENT_ALREADY_COMPLETED'],
  // currency ↔ country coherence (ms-payments). El backend emite la clave
  // cruda `currencyCountryMismatch` para evitar acoplamiento a PLAN.ERRORS/
  // PACK.ERRORS; el path i18n vive en `plansAndPacks.errors.*` que ya consumen
  // los forms de plan y pack.
  ['currencyCountryMismatch', 'plansAndPacks.errors.currencyCountryMismatch'],
]);

const STATUS_ERROR_MESSAGES = new Map<number, string>([
  [403, 'Access forbidden'],
  [404, 'Resource not found'],
  [429, 'Too many requests'],
  [500, 'Internal server error'],
]);

class HttpClient {
  private instance: AxiosInstance;
  private baseURL: string;
  // Rutas que no requieren token de autenticación
  private authRoutes: string[] = ['/auth/login', '/auth/signin', '/auth/register', '/auth/forgot-password'];
  // Rutas de token público que no necesitan JWT (usan token en URL)
  private _publicTokenRoutes: string[] = ['/documents/signatures/token/'];
  // Rutas cuyo 401 NO debe disparar logout (facial verification, firma por token, etc.)
  private readonly _noLogoutRoutes: string[] = ['/verify-identity', '/initiate-facial-verification', '/documents/signatures/token/'];
  private _isLogoutSuppressed = false;
  private _suppressionTimeout: ReturnType<typeof setTimeout> | null = null;
  // Referencia al store de notificaciones
  private notificationStore = useNotificationStore.getState();

  constructor(config?: IHttpClientConfig) {
    this.baseURL = config?.baseURL || process.env.NEXT_PUBLIC_API_BASE_URL || '';

    // Create Axios instance with default configuration
    this.instance = axios.create({
      baseURL: this.baseURL,
      timeout: config?.timeout || 1800000, // Aumentado a 30 minutos para consultas lentas
      headers: {
        ['Content-Type']: 'application/json',
      },
    });

    // Request Interceptor for logging and token injection
    this.instance.interceptors.request.use(
      (request) => {
        // Log request details. El payload y las cabeceras pasan siempre por la
        // denylist de campos: `Authorization`, `password`, tokens y material de
        // certificado no pueden acabar en la consola del navegador.
        //
        // El antiguo `[HTTP Payload]` a nivel `info` se eliminó: duplicaba este
        // mismo cuerpo, pero a un nivel que sí se emite en producción, así que
        // cada request dejaba su payload completo en el log del cliente.
        logger.debug(`HTTP Request ${request.method?.toUpperCase()} ${request.url}`, {
          module: 'http-client',
          data: redactLoggablePayload(request.url, request.data),
          headers: redactLoggablePayload(request.url, request.headers),
        });

        // Verificar si la ruta actual es una ruta de autenticación o de token público
        const currentUrl = request.url || '';
        const isAuthRoute = this.authRoutes.some((route) => currentUrl.includes(route));
        const isPublicTokenRoute = this._publicTokenRoutes.some((route) => currentUrl.includes(route));

        const hasAuthorizationHeader = Boolean(request.headers.Authorization ?? request.headers.authorization);

        // Inyectar token solo si la request no trae Authorization explícito.
        // Login social usa Basic y /auth/verify manda un Bearer específico:
        // pisarlos con una sesión persistida vieja revive tokens inválidos.
        if (!hasAuthorizationHeader && !isAuthRoute && !isPublicTokenRoute) {
          const token = this.getAuthToken();
          if (token) {
            request.headers['Authorization'] = `Bearer ${token}`;
          }
        }

        return request;
      },
      (error) => {
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        logger.error('[HTTP Request Error]', normalizedError, {
          module: 'httpClient',
          stage: 'requestInterceptor',
        });
        return Promise.reject(error);
      }
    );

    // Response Interceptor for logging and error handling
    this.instance.interceptors.response.use(
      (response: AxiosResponse<IResponseMessage>) => {
        // La respuesta de `/auth/signin` trae `access_token` y `refresh_token`:
        // se redactan por nombre de campo igual que el payload de ida.
        logger.debug(`HTTP Response ${response.status} ${response.config.url}`, {
          module: 'http-client',
          data: redactLoggablePayload(response.config.url, response.data),
        });

        // Mostrar mensaje de éxito si existe
        if (response.data && typeof response.data === 'object' && 'message' in response.data) {
          // Actualizar el store para mostrar la notificación
          this.notificationStore = useNotificationStore.getState();

          // Obtener el mensaje
          const message = response.data.message;

          if (typeof message === 'string') {
            // Verificar si el mensaje necesita ser traducido
            // 1. Si comienza con "common." es una clave de traducción
            // 2. Si contiene "." pero no espacios, probablemente es una clave de traducción
            // 3. Si coincide con alguno de los códigos de éxito conocidos, usar la traducción correspondiente
            let finalMessage = message;
            let isTranslationKey = false;

            // Comprobar si es una clave de traducción directa
            if (message.startsWith('common.') || (message.includes('.') && !message.includes(' '))) {
              isTranslationKey = true;
            }
            // Mapear códigos de éxito conocidos a claves de traducción
            else if (message === 'RECOVERY_CODE_SENT') {
              finalMessage = 'common.AUTH.MESSAGES.RECOVERY_CODE_SENT';
              isTranslationKey = true;
            } else if (message === 'PASSWORD_CHANGED') {
              finalMessage = 'common.AUTH.MESSAGES.PASSWORD_CHANGED';
              isTranslationKey = true;
            } else {
              // Para mensajes genéricos, mantener el mensaje original
              isTranslationKey = false;
            }

            this.notificationStore.showSuccessNotification(finalMessage, 5000, isTranslationKey);
          }
        }

        return response;
      },
      (error) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      }
    );
  }

  // Retrieve authentication token from local storage
  private getAuthToken(): string | null {
    const token = useAuthStore.getState().token;
    return token ? token : null;
  }

  /**
   * Procesa el mensaje de error de la respuesta de la API
   * @param messageData El mensaje de error que puede ser string, array o `{ message, code }`
   * @returns Un objeto con el mensaje final y si es una clave de traducción
   */
  private processErrorMessage(messageData: unknown): { finalMessage: string; isTranslationKey: boolean } {
    const { message, code } = extractErrorDetails(messageData);

    const translatedCode = this.getErrorTranslationKey(code);
    if (translatedCode) {
      return { finalMessage: translatedCode, isTranslationKey: true };
    }

    if (!message) {
      return { finalMessage: 'common.ERRORS.UNEXPECTED_ERROR', isTranslationKey: true };
    }

    if (isDirectTranslationKey(message)) {
      return {
        finalMessage: message.startsWith('common.') ? message : `common.${message}`,
        isTranslationKey: true,
      };
    }

    const translatedMessage = KNOWN_ERROR_TRANSLATIONS.get(message);
    if (translatedMessage) {
      return { finalMessage: translatedMessage, isTranslationKey: true };
    }

    if (this.isNetworkErrorMessage(message)) {
      return { finalMessage: 'common.ERRORS.NETWORK_ERROR', isTranslationKey: true };
    }

    if (this.isBulkValidationMessage(message)) {
      return { finalMessage: '', isTranslationKey: false };
    }

    // El 404 de NestJS para una ruta inexistente («Cannot POST /api/…») habla
    // de la API, no del usuario: nunca se muestra tal cual.
    if (/^Cannot (GET|POST|PUT|PATCH|DELETE) \//.test(message)) {
      return { finalMessage: 'common.ERRORS.UNEXPECTED_ERROR', isTranslationKey: true };
    }

    if (message.includes(' ')) {
      return { finalMessage: message, isTranslationKey: false };
    }

    return { finalMessage: 'common.ERRORS.UNEXPECTED_ERROR', isTranslationKey: true };
  }

  // Handle response errors
  private handleResponseError(error: AxiosError<IResponseErrorApi>) {
    if (error.response) {
      this.handleResponseErrorWithResponse(error);
      return;
    }

    if (error.request) {
      this.handleResponseErrorWithoutResponse(error);
      return;
    }

    this.handleResponseSetupError(error);
  }

  private handleResponseErrorWithResponse(error: AxiosError<IResponseErrorApi>): void {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    const responseMessage = extractErrorDetails(error.response?.data?.message);
    const messageStr = responseMessage.message ?? responseMessage.code ?? '';
    const isBulkValidation = this.isBulkValidationMessage(messageStr);
    const isSensitiveRequest = isSensitiveEmailTemplateRequest(error.config?.url);
    const isExpectedEntitlementError =
      isExpectedEmailTemplateEntitlementError({
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message,
      }) || isExpectedTraceNotFound({ url: error.config?.url, status: error.response?.status });

    if (isExpectedEntitlementError) {
      logger.debug('[HTTP Expected Entitlement Denied]', {
        module: 'httpClient',
        stage: 'responseInterceptor',
        status: error.response?.status,
      });
    } else if (isBulkValidation) {
      logger.warn('[HTTP Bulk Validation Error]', {
        module: 'httpClient',
        stage: 'responseInterceptor',
        status: error.response?.status,
      });
    } else {
      logger.error('[HTTP Response Error]', normalizedError, {
        module: 'httpClient',
        stage: 'responseInterceptor',
        status: error.response?.status,
        data: redactLoggablePayload(error.config?.url, error.response?.data),
        headers: redactLoggablePayload(error.config?.url, error.response?.headers),
      });
      addFrontendBreadcrumb('[HTTP Response Error]', {
        module: 'httpClient',
        stage: 'responseInterceptor',
        status: error.response?.status,
        method: error.config?.method?.toUpperCase(),
        url: isSensitiveRequest ? REDACTED : error.config?.url,
        path: isSensitiveRequest ? REDACTED : error.response?.data?.path,
        apiMessage: isSensitiveRequest ? REDACTED : messageStr,
        innerError: redactLoggablePayload(error.config?.url, error.response?.data?.innerError),
      });
    }

    // 428 Precondition Required no es un error que mostrarle al usuario: le
    // dice al cliente que falta cumplir un paso previo (hoy, verificar el
    // código enviado por correo antes de firmar). Quien lo recibe abre la UI
    // de ese paso; un toast acá solo agrega ruido antes del modal.
    //
    // El 429 del reenvío de código sigue la misma lógica: solo el modal conoce
    // los segundos que faltan, así que es él quien avisa; el toast genérico de
    // acá sería un segundo mensaje diciendo lo mismo pero peor.
    const isSilencedStatus =
      error.response?.status === HTTP_PRECONDITION_REQUIRED ||
      (error.response?.status === RESEND_COOLDOWN_STATUS && hasResendCooldownMessage(error));

    if (!isSilencedStatus && !isExpectedEntitlementError) {
      this.showResponseErrorNotification(error.response?.data);
    }

    logger.debug('HTTP Response Error Details', {
      module: 'http-client',
      response: redactLoggablePayload(error.config?.url, error.response),
    });
    this.handleStatusSpecificError(error);
  }

  private handleResponseErrorWithoutResponse(error: AxiosError<IResponseErrorApi>): void {
    const isSensitiveRequest = isSensitiveEmailTemplateRequest(error.config?.url);
    logger.error('[HTTP No Response]', undefined, {
      module: 'httpClient',
      stage: 'responseInterceptor',
      detail: 'Request sent but no response received',
    });
    reportFrontendError(
      '[HTTP No Response]',
      isSensitiveRequest ? new Error('Sensitive email template request failed without response') : error,
      {
        module: 'httpClient',
        stage: 'responseInterceptor',
        status: 0,
        method: error.config?.method?.toUpperCase(),
        url: isSensitiveRequest ? REDACTED : error.config?.url,
        escalate: true,
      }
    );

    this.notificationStore = useNotificationStore.getState();
    this.notificationStore.showErrorNotification('common.ERRORS.NETWORK_ERROR', 5000, true);
  }

  private handleResponseSetupError(error: AxiosError<IResponseErrorApi>): void {
    const normalizedSetupError = error instanceof Error ? error : new Error(String(error));
    logger.error('[HTTP Request Setup Error]', normalizedSetupError, {
      module: 'httpClient',
      stage: 'responseInterceptor',
    });
    reportFrontendError('[HTTP Request Setup Error]', normalizedSetupError, {
      module: 'httpClient',
      stage: 'responseInterceptor',
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      escalate: true,
    });

    this.notificationStore = useNotificationStore.getState();
    this.notificationStore.showErrorNotification('common.ERRORS.UNEXPECTED_ERROR', 5000, true);
  }

  private showResponseErrorNotification(errorData?: IResponseErrorApi): void {
    if (!errorData || typeof errorData !== 'object' || !('message' in errorData)) {
      return;
    }

    this.notificationStore = useNotificationStore.getState();
    const { finalMessage, isTranslationKey } = this.processErrorMessage(errorData.message);

    if (finalMessage) {
      this.notificationStore.showErrorNotification(finalMessage, 8000, isTranslationKey);
    }
  }

  private handleStatusSpecificError(error: AxiosError<IResponseErrorApi>): void {
    const status = error.response?.status;

    if (
      isExpectedEmailTemplateEntitlementError({
        url: error.config?.url,
        status,
        message: error.response?.data?.message,
      })
    ) {
      return;
    }

    if (status === 401) {
      this.handleUnauthorized(error.response?.data, error.config?.url);
      return;
    }

    const statusMessage = status ? STATUS_ERROR_MESSAGES.get(status) : undefined;
    if (!statusMessage) {
      return;
    }

    logger.error(statusMessage, undefined, {
      module: 'httpClient',
      stage: 'responseInterceptor',
      status,
    });
  }

  private getErrorTranslationKey(code?: string): string | undefined {
    if (!code) return undefined;

    const knownTranslation = KNOWN_ERROR_TRANSLATIONS.get(code);
    if (knownTranslation) return knownTranslation;
    if (!isDirectTranslationKey(code)) return undefined;

    return code.startsWith('common.') ? code : `common.${code}`;
  }

  private isNetworkErrorMessage(message: string): boolean {
    return message.includes('NETWORK_ERROR') || message.includes('ECONNREFUSED');
  }

  private isBulkValidationMessage(message: string): boolean {
    return /rows\.\d+\./.test(message);
  }

  // Handle unauthorized access
  private handleUnauthorized(errorData?: IResponseErrorApi, requestUrl?: string) {
    const isSensitiveRequest = isSensitiveEmailTemplateRequest(requestUrl);
    if (!this.shouldLogoutOnUnauthorized(errorData, requestUrl)) {
      logger.warn('Unauthorized response received without session invalidation', {
        module: 'http-client',
        code: isSensitiveRequest ? REDACTED : this.getErrorCode(errorData),
        message: isSensitiveRequest ? REDACTED : this.getErrorMessage(errorData),
        requestUrl: isSensitiveRequest ? REDACTED : requestUrl,
      });
      return;
    }

    logger.warn('Unauthorized access detected — logging out', {
      module: 'http-client',
      code: isSensitiveRequest ? REDACTED : this.getErrorCode(errorData),
      message: isSensitiveRequest ? REDACTED : this.getErrorMessage(errorData),
      requestUrl: isSensitiveRequest ? REDACTED : requestUrl,
    });
    useAuthStore.getState().logout();
  }

  private shouldLogoutOnUnauthorized(errorData?: IResponseErrorApi, requestUrl?: string): boolean {
    // Skip logout for facial verification / token-based routes
    if (requestUrl && this._noLogoutRoutes.some((route) => requestUrl.includes(route))) {
      return false;
    }

    // Skip logout during temporal suppression (e.g., returning from Didit)
    if (this._isLogoutSuppressed) {
      return false;
    }

    const code = this.getErrorCode(errorData);
    const message = this.getErrorMessage(errorData);

    if (code === 'LINK_EXPIRED') {
      return false;
    }

    return [
      'Unauthorized',
      'AUTH.ERRORS.INVALID_TOKEN',
      'Token not found',
      'jwt expired',
      'invalid signature',
      'jwt malformed',
      'invalid token',
    ].includes(message);
  }

  /**
   * Temporarily suppress logout on 401 responses.
   * Used when returning from external providers (e.g., Didit) where background
   * query refetches may hit 401 before the user context is re-established.
   */
  suppressLogout(durationMs = 10000): void {
    this._isLogoutSuppressed = true;
    if (this._suppressionTimeout) {
      clearTimeout(this._suppressionTimeout);
    }
    this._suppressionTimeout = setTimeout(() => {
      this._isLogoutSuppressed = false;
      this._suppressionTimeout = null;
    }, durationMs);
    logger.info('Logout suppression activated', { module: 'http-client', durationMs });
  }

  /**
   * Manually end logout suppression early.
   */
  unsuppressLogout(): void {
    this._isLogoutSuppressed = false;
    if (this._suppressionTimeout) {
      clearTimeout(this._suppressionTimeout);
      this._suppressionTimeout = null;
    }
  }

  private getErrorCode(errorData?: IResponseErrorApi): string | undefined {
    return extractErrorDetails(errorData).code;
  }

  private getErrorMessage(errorData?: IResponseErrorApi): string {
    const { message, code } = extractErrorDetails(errorData);
    return message ?? code ?? '';
  }

  /**
   * Ensures the URL is properly formatted with the base URL if it's a relative path
   * @param url The URL or path to format
   * @returns Properly formatted URL
   */
  private formatUrl(url: string): string {
    // If URL already starts with http:// or https://, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Ensure path starts with a slash
    const path = url.startsWith('/') ? url : `/${url}`;

    return path;
  }

  // Generic GET request
  async get<TData>(url: string, config?: AxiosRequestConfig): Promise<IApiResponse<TData>> {
    try {
      const formattedUrl = this.formatUrl(url);
      const response: AxiosResponse<TData> = await this.instance.get(formattedUrl, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic POST request
  async post<TPayload, TResponse>(url: string, data: TPayload, config?: AxiosRequestConfig): Promise<IApiResponse<TResponse>> {
    try {
      const formattedUrl = this.formatUrl(url);
      const response: AxiosResponse<TResponse> = await this.instance.post(formattedUrl, data, config);
      return {
        data: response.data,
        status: response.status,
        headers: response.headers as Record<string, string | undefined>,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic PUT request
  async put<TPayload, TResponse>(url: string, data: TPayload, config?: AxiosRequestConfig): Promise<IApiResponse<TResponse>> {
    try {
      const formattedUrl = this.formatUrl(url);
      const response: AxiosResponse<TResponse> = await this.instance.put(formattedUrl, data, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic PATCH request
  async patch<TPayload, TResponse>(url: string, data: TPayload, config?: AxiosRequestConfig): Promise<IApiResponse<TResponse>> {
    try {
      const formattedUrl = this.formatUrl(url);
      const response: AxiosResponse<TResponse> = await this.instance.patch(formattedUrl, data, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic DELETE request
  async delete<TData>(url: string, config?: AxiosRequestConfig): Promise<IApiResponse<TData>> {
    try {
      const formattedUrl = this.formatUrl(url);
      const response: AxiosResponse<TData> = await this.instance.delete(formattedUrl, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Error handling for all requests
  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      const errorDetails = extractErrorDetails(error.response?.data?.message);
      throw {
        data: error.response?.data,
        status: error.response?.status || 0,
        message: errorDetails.message ?? 'An error occurred',
      };
    } else if (error instanceof Error) {
      throw {
        data: null,
        status: 0,
        message: error.message || 'Request setup error',
      };
    } else {
      throw {
        data: null,
        status: 0,
        message: 'An unexpected error occurred',
      };
    }
  }

  /**
   * Get the current base URL
   * @returns The base URL being used for requests
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Set a new base URL for all future requests
   * @param newBaseURL The new base URL to use
   */
  setBaseURL(newBaseURL: string): void {
    this.baseURL = newBaseURL;
    this.instance.defaults.baseURL = newBaseURL;
  }
}

// Create a singleton instance
export const httpClient = new HttpClient();
export default httpClient;
